// MCP ツールの一覧メタデータ（単一の正本）。
//
// サーバー（apps/api の tools/list フィルタ・execute の権限判定）と、Web の MCP 設定
// 手順ページの双方がこの定義を参照する。UI とサーバーでツール一覧や必要権限が食い違う
// のを防ぐため、ツール名・要約・必要権限はここへ一元化する。
// 各ツールの詳細な inputSchema はサーバー側（apps/api/src/mcp/tools.ts）が持つ。

/** ツール表示・実行に必要な権限種別。 */
export type McpToolPermission = "any" | "read" | "write" | "admin";

/** キャンバスの権限フラグ（RequestActor.permissions / フロントのセッション権限と同形）。 */
export interface McpCanvasPermissions {
  readonly canRead: boolean;
  readonly canWrite: boolean;
  readonly canAdmin: boolean;
}

export interface McpToolOverview {
  readonly name: string;
  /** 設定ページのツール表に出す日本語の要約。 */
  readonly summary: string;
  readonly permission: McpToolPermission;
}

export const MCP_TOOLS_OVERVIEW: readonly McpToolOverview[] = [
  {
    name: "whoami",
    summary: "ログイン中のユーザーと権限（閲覧/編集/管理）を確認します。",
    permission: "any",
  },
  {
    name: "list_canvases",
    summary:
      "アクセスできるキャンバスの一覧を返します（一般ユーザーは履歴+お気に入り、管理者は全件。ゴミ箱は除外）。",
    permission: "read",
  },
  {
    name: "search_canvases",
    summary:
      "ゴミ箱を除き、付箋・テキスト等の中身も含めてキャンバスを横断検索し、抜粋付きで返します。",
    permission: "read",
  },
  {
    name: "get_canvas",
    summary:
      "指定キャンバスのタイトル・ゴミ箱状態と全オブジェクト（付箋・図形・テキスト等）を取得します。",
    permission: "read",
  },
  {
    name: "create_canvas",
    summary: "新規キャンバスを作成し、任意の初期オブジェクト（付箋・図形・テキスト等）を配置します。",
    permission: "write",
  },
  {
    name: "add_canvas_objects",
    summary:
      "既存キャンバスへ付箋・図形・テキスト・コネクタなどをまとめて追加します（ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "update_canvas_object",
    summary: "既存オブジェクトの位置・サイズ・テキスト・色などを変更します（ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "delete_canvas_objects",
    summary: "既存オブジェクトを削除します（接続コネクタも併せて削除。ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "rename_canvas",
    summary: "キャンバスのタイトルを変更します（ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "list_comments",
    summary: "指定キャンバスのコメント（スレッドと各メッセージ）を一覧します。",
    permission: "read",
  },
  {
    name: "create_comment_thread",
    summary:
      "キャンバス上の指定位置に、起点コメント付きのコメントスレッドを作成します（ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "reply_comment",
    summary: "既存のコメントスレッドへ返信を追加します（ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "edit_comment",
    summary:
      "コメントスレッド内の自分のメッセージ本文を編集します（管理者は任意。ゴミ箱は変更不可）。",
    permission: "write",
  },
  {
    name: "delete_comment",
    summary:
      "コメントスレッド内の自分のメッセージを削除します（管理者は任意。全削除でスレッドも消去。ゴミ箱は変更不可）。",
    permission: "write",
  },
];

/** 指定権限のアクターに、当該ツールを公開・実行してよいか判定する。 */
export function isMcpToolVisible(
  tool: McpToolOverview,
  permissions: McpCanvasPermissions,
): boolean {
  switch (tool.permission) {
    case "any":
      return true;
    case "read":
      return permissions.canRead;
    case "write":
      return permissions.canWrite;
    case "admin":
      return permissions.canAdmin;
  }
}
